// Lib
import React, { useMemo } from 'react';

// Components
import Icon from '../../../../../components/icons/Icon';
import SelectInput from '../../../../../components/select/SelectInput';

// Constants
import { REMINDER_VALUES } from './taskDueDateConstants';

// Styles
import './DueDateReminderSelect.scss';
import { useTranslator } from "../../../../../components/i18n/useTranslator";

type ReminderValue = keyof typeof REMINDER_VALUES | string;

interface DueDateReminderSelectProps {
    value: string;
    onChange: (updateReminder: string) => void;
    hasDueDateTime: boolean;
    locale?: string;
}

const getReminderOptions = (hasDueDateTime: boolean): ReminderValue[] =>
    hasDueDateTime
        ? [
              REMINDER_VALUES.NONE,
              REMINDER_VALUES.WHEN_DUE,
              REMINDER_VALUES.TEN_MINUTES_BEFORE,
              REMINDER_VALUES.ONE_HOUR_BEFORE,
              REMINDER_VALUES.NINE_AM_DAY_OF,
              REMINDER_VALUES.NINE_AM_DAY_BEFORE,
          ]
        : [REMINDER_VALUES.NONE, REMINDER_VALUES.NINE_AM_DAY_OF, REMINDER_VALUES.NINE_AM_DAY_BEFORE];

/**
 * Formats a time for display in reminder options using the user's locale
 * This ensures times are formatted correctly for different locales (e.g., "9am" in English, "9 a.m." in Spanish)
 */
const getFormattedTime = (hour: number, locale: string): string => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);

    // Use the locale's native time formatting
    // This will automatically handle spacing, punctuation, and AM formatting
    return new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        hourCycle: 'h12',
    }).format(date);
};

const DueDateReminderSelect = ({ value, onChange, hasDueDateTime, locale = 'en-US' }: DueDateReminderSelectProps) => {
    const translate = useTranslator('popup', 'assignment.dueDate.reminder');
    const reminderOptions = getReminderOptions(hasDueDateTime);

    // Format the time once and reuse it
    const formattedTime = useMemo(() => getFormattedTime(9, locale), [locale]);

    const REMINDER_TRANSLATION_KEYS: Record<string, string> = {
        [REMINDER_VALUES.NONE]: 'none',
        [REMINDER_VALUES.WHEN_DUE]: 'whenDue',
        [REMINDER_VALUES.TEN_MINUTES_BEFORE]: 'tenMinutesBefore',
        [REMINDER_VALUES.ONE_HOUR_BEFORE]: 'oneHourBefore',
        [REMINDER_VALUES.NINE_AM_DAY_OF]: 'nineAmDayOf',
        [REMINDER_VALUES.NINE_AM_DAY_BEFORE]: 'nineAmDayBefore',
    };

    const onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        onChange(event.target.value);
    };

    return (
        <div className="DueDateReminderSelect" style={{ cursor: 'pointer' }}>
            <div className="bell-icon-container">
                <Icon name="reminder-bell" />
            </div>
            <SelectInput className="select-input" value={value} onChange={onSelectChange}>
                {reminderOptions.map((reminderOption, i) => {
                    const translationKey = REMINDER_TRANSLATION_KEYS[reminderOption];

                    // For time-based reminders, pass the formatted time as a variable
                    if (reminderOption === REMINDER_VALUES.NINE_AM_DAY_OF || reminderOption === REMINDER_VALUES.NINE_AM_DAY_BEFORE) {
                        return (
                            <option key={i} value={reminderOption}>
                                {translate(translationKey, { time: formattedTime })}
                            </option>
                        );
                    }

                    return (
                        <option key={i} value={reminderOption}>
                            {translate(translationKey)}
                        </option>
                    );
                })}
            </SelectInput>
        </div>
    );
};

export default DueDateReminderSelect;
